import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Upload, Database, Trash2, Download, Search, Edit2, Plus, X, Save} from 'lucide-react'
import { useDatosCompletosCRUD } from '../hooks/useDatosCompletosCRUD'

const DatosCompletos = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [formData, setFormData] = useState<any>({})

  const {
    datosCompletos,
    loading,
    uploading,
    createDato,
    updateDato,
    deleteDato,
    uploadExcelFromURL,
    uploadExcelFile,
    clearAllData
  } = useDatosCompletosCRUD()

  const filteredData = datosCompletos.filter(item => {
    const searchLower = searchTerm.toLowerCase()
    return (
      item.codigo?.toLowerCase().includes(searchLower) ||
      item.nombre_microcuenca?.toLowerCase().includes(searchLower) ||
      item.municipio?.toLowerCase().includes(searchLower)
    )
  })

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        await uploadExcelFile(file)
      } else {
        alert('Por favor selecciona un archivo Excel válido (.xlsx o .xls)')
      }
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        await uploadExcelFile(file)
      } else {
        alert('Por favor selecciona un archivo Excel válido (.xlsx o .xls)')
      }
      e.target.value = ''
    }
  }

  const handleImportFromURL = async () => {
    const url = 'https://file.lumi.new/p373487986422038528/SPF2011175686908739584.xlsx'
    await uploadExcelFromURL(url)
  }

  const handleEdit = (item: any) => {
    setEditingItem(item)
    setFormData(item)
    setShowModal(true)
  }

  const handleCreate = () => {
    setEditingItem(null)
    setFormData({
      fid: 0,
      codigo: '',
      nombre_microcuenca: '',
      municipio: '',
      superficie_agricola: 0,
      superficie_microcuenca: 0,
      superficie_pasto: 0,
      superficie_forestal: 0,
      densidad_poblacion: 0,
      poblacion_total: 0,
      indice_marginacion: 0,
      grado_marginacion: '',
      indice_rezago: 0,
      clase_rezago: '',
      precipitacion: 0,
      temperatura: 0,
      altitud: 0,
      pendiente: 0,
      superficie_deforestada: 0,
      erosion: 0,
      categoria_erosion: '',
      nivel_erosion: 0,
      ponderacion_escurrimiento: 0,
      hectareas_anp: 0
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    if (editingItem) {
      await updateDato(editingItem._id, formData)
    } else {
      await createDato(formData)
    }
    setShowModal(false)
  }

  const handleDelete = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este registro?')) {
      await deleteDato(id)
    }
  }

  const handleClearAll = async () => {
    if (confirm('¿Estás seguro de eliminar TODOS los registros? Esta acción no se puede deshacer.')) {
      await clearAllData()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver al Inicio</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Database className="text-white w-4 h-4" />
                </div>
                <h1 className="text-xl font-bold text-gray-800">Datos Completos de Microcuencas</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleCreate}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Registro</span>
              </button>
              <button 
                onClick={handleImportFromURL}
                disabled={uploading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                <span>Importar Archivo</span>
              </button>
              {datosCompletos.length > 0 && (
                <button 
                  onClick={handleClearAll}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Eliminar Todo</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-6 py-8">
        {/* Zona de Carga */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">Cargar Archivo Excel</h2>
          
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {uploading ? 'Procesando archivo...' : 'Arrastra tu archivo Excel aquí'}
            </h3>
            <p className="text-gray-600 mb-4">O haz clic para seleccionar un archivo</p>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Cargando...' : 'Seleccionar Archivo'}
            </button>
          </div>
        </motion.div>

        {/* Búsqueda */}
        {datosCompletos.length > 0 && (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white rounded-xl shadow-lg p-6 mb-8"
          >
            <div className="flex items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar por código, microcuenca o municipio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-full"
                />
              </div>
              <div className="text-sm text-gray-600">
                {filteredData.length} de {datosCompletos.length} registros
              </div>
            </div>
          </motion.div>
        )}

        {/* Tabla */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredData.length === 0 ? (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white rounded-xl shadow-lg p-12 text-center"
          >
            <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No hay datos disponibles</h3>
            <p className="text-gray-600">Carga un archivo Excel para comenzar</p>
          </motion.div>
        ) : (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Microcuenca</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Municipio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sup. Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Población</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precipitación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{item.codigo}</td>
                      <td className="px-4 py-3 text-sm">{item.nombre_microcuenca}</td>
                      <td className="px-4 py-3 text-sm">{item.municipio}</td>
                      <td className="px-4 py-3 text-sm">{item.superficie_microcuenca?.toFixed(1)} ha</td>
                      <td className="px-4 py-3 text-sm">{item.poblacion_total?.toFixed(0)}</td>
                      <td className="px-4 py-3 text-sm">{item.precipitacion?.toFixed(1)} mm</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item._id!)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>

      {/* Modal de Edición */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {editingItem ? 'Editar Registro' : 'Nuevo Registro'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Código</label>
                <input
                  type="text"
                  value={formData.codigo || ''}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Municipio</label>
                <input
                  type="text"
                  value={formData.municipio || ''}
                  onChange={(e) => setFormData({...formData, municipio: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Nombre Microcuenca</label>
                <input
                  type="text"
                  value={formData.nombre_microcuenca || ''}
                  onChange={(e) => setFormData({...formData, nombre_microcuenca: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Superficie Agrícola</label>
                <input
                  type="number"
                  value={formData.superficie_agricola || 0}
                  onChange={(e) => setFormData({...formData, superficie_agricola: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Superficie Microcuenca</label>
                <input
                  type="number"
                  value={formData.superficie_microcuenca || 0}
                  onChange={(e) => setFormData({...formData, superficie_microcuenca: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Población Total</label>
                <input
                  type="number"
                  value={formData.poblacion_total || 0}
                  onChange={(e) => setFormData({...formData, poblacion_total: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Precipitación</label>
                <input
                  type="number"
                  value={formData.precipitacion || 0}
                  onChange={(e) => setFormData({...formData, precipitacion: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Temperatura</label>
                <input
                  type="number"
                  value={formData.temperatura || 0}
                  onChange={(e) => setFormData({...formData, temperatura: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Erosión</label>
                <input
                  type="number"
                  value={formData.erosion || 0}
                  onChange={(e) => setFormData({...formData, erosion: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Guardar</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default DatosCompletos
